"use client";

import React, { useEffect, useState, useRef } from "react";

/**
 * StaticOverlay - Creates a CRT TV effect with scanlines and static noise
 * Used for the STATIC hazard in Hazard mode
 */
const StaticOverlay: React.FC = () => {
    const basicStaticIntensity = 0.5;
    const angryStaticIntensity = 0.7;
    const [glitchOffset, setGlitchOffset] = useState(0);
    const [wavePosition, setWavePosition] = useState(0);
    const [staticIntensity, setStaticIntensity] = useState(basicStaticIntensity);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Generate TV static on canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        canvas.width = 1000;
        canvas.height = 1000;

        const generateStatic = () => {
            const imageData = ctx.createImageData(canvas.width, canvas.height);
            const data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
                const value = Math.random() * 255;
                data[i] = value;     // R
                data[i + 1] = value; // G
                data[i + 2] = value; // B
                data[i + 3] = 255;   // A
            }

            ctx.putImageData(imageData, 0, 0);
        };

        // Update static more frequently for better effect
        const staticInterval = setInterval(generateStatic, 50);
        generateStatic(); // Initial render

        return () => clearInterval(staticInterval);
    }, []);

    useEffect(() => {
        // Glitch effect - occasional horizontal displacement
        const glitchInterval = setInterval(() => {
            if (Math.random() > 0.85) { // 15% chance of glitch
                setGlitchOffset(Math.random() * 10 - 5);
                setTimeout(() => setGlitchOffset(0), 50 + Math.random() * 100);
            }
        }, 100);

        return () => clearInterval(glitchInterval);
    }, []);

    useEffect(() => {
        // Wave scrolling effect - moves from top to bottom
        let animationFrameId: number;
        const startTime = Date.now();

        const animateWave = () => {
            const elapsed = Date.now() - startTime;
            // Complete cycle every 3 seconds
            const progress = (elapsed % 3000) / 3000;
            setWavePosition(progress * 100);
            animationFrameId = requestAnimationFrame(animateWave);
        };

        animationFrameId = requestAnimationFrame(animateWave);

        return () => cancelAnimationFrame(animationFrameId);
    }, []);

    useEffect(() => {
        // Random static intensity bursts
        const burstInterval = setInterval(() => {
            if (Math.random() > 0.7) { // 30% chance of burst
                setStaticIntensity(angryStaticIntensity); // Much stronger static
                setTimeout(() => setStaticIntensity(basicStaticIntensity), 800 + Math.random() * 400); // Back to normal after 0.8-1.2s
            }
        }, 2000 + Math.random() * 1000); // Every 2-3 seconds

        return () => clearInterval(burstInterval);
    }, []);

    return (
        <>
            {/* CRT Screen Curvature Simulation */}
            <div
                className="absolute inset-0 w-full h-full pointer-events-none z-[12]"
                style={{
                    background: 'transparent',
                    boxShadow: 'inset 0 0 100px rgba(0,0,0,0.5), inset 0 0 200px rgba(0,0,0,0.2)',
                    /* This creates the slight rounding of the corners */
                    borderRadius: '10%',
                    transform: 'scale(1.05)', // Scale slightly to hide the rounded edges of the container
                }}
            />
            {/* Scanlines overlay */}
            <div
                className="absolute inset-0 w-full h-full pointer-events-none z-[10]"
                style={{
                    backgroundImage: `repeating-linear-gradient(
                        0deg,
                        rgba(0, 0, 0, 0.35) 0px,
                        rgba(0, 0, 0, 0.35) 2px,
                        transparent 2px,
                        transparent 4px
                    )`,
                    transform: `translateX(${glitchOffset}px)`,
                    transition: 'transform 0.05s',
                }}
            />

            {/* Wave distortion effect - scrolls from top to bottom */}
            <div
                className="absolute inset-0 w-full h-full pointer-events-none z-[10] backdrop-blur-[1px]"
                style={{
                    background: `linear-gradient(180deg, 
                        transparent 0%, 
                        transparent ${Math.max(0, wavePosition - 8)}%, 
                        rgba(255, 255, 255, 0.1) ${Math.max(0, wavePosition - 4)}%,
                        rgba(255, 255, 255, 0.2) ${wavePosition}%, 
                        rgba(255, 255, 255, 0.1) ${Math.min(100, wavePosition + 4)}%,
                        transparent ${Math.min(100, wavePosition + 8)}%, 
                        transparent 100%
                    )`,
                }}
            />

            {/* TV Static noise - canvas-based black and white pixels */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none z-[10]"
                style={{
                    opacity: staticIntensity,
                    imageRendering: 'pixelated',
                    mixBlendMode: 'overlay',
                    transition: 'opacity 0.2s',
                }}
            />

            {/* Vignette */}
            <div
                className="absolute inset-0 w-full h-full pointer-events-none z-[10]"
                style={{
                    background: `radial-gradient(ellipse at center, transparent 0%, transparent 60%, rgba(0, 0, 0, 0.15) 80%, rgba(0, 0, 0, 0.4) 100%)`,
                    boxShadow: 'inset 0 0 100px rgba(0, 0, 0, 0.5)',
                }}
            />

            {/* Occasional RGB chromatic aberration glitch */}
            {glitchOffset !== 0 && (
                <div
                    className="absolute inset-0 w-full h-full pointer-events-none z-[11]"
                    style={{
                        background: `
                            linear-gradient(90deg, 
                                rgba(255, 0, 0, 0.15) 0%, 
                                transparent 2%, 
                                transparent 98%, 
                                rgba(0, 0, 255, 0.15) 100%
                            )
                        `,
                        transform: `translateX(${glitchOffset * 2}px)`,
                        opacity: 0.5,
                    }}
                />
            )}

            {/* Desaturate greenish tint overlay */}
            <div
                className="absolute inset-0 w-full h-full pointer-events-none z-[9]"
                style={{
                    background: 'rgba(0, 50, 0, 0.2)',
                    mixBlendMode: 'color',
                }}
            />
        </>
    );
};

export default StaticOverlay;
